import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MdOutlineWarningAmber,
  MdOutlinePhoneMissed,
  MdOutlinePhoneForwarded,
  MdOutlineTimer,
} from "react-icons/md";

import BarChartSection from "./components/BarChartSection";
import TopFlopTable from "./components/TopFlopTable";
import LineChartSection from "./components/LineChartSection";
import CallPurposeSection from "./components/CallPurposeSection";
import CallsHeatmapSection from "./components/CallsHeatmapSection";
import CallsGaugeBar from "../../components/dashboard/CallsGaugeBar";
import KpiStatCard from "../../components/dashboard/KpiStatCard";
import CallDirectionCard from "../../components/dashboard/CallDirectionCard";

import { initAnalytics } from "../../redux/actions";
import { useAppContext } from "../../contexts/AppContext";
import { transformToUTC } from "../../utils/dateUtils";

// Seconds -> "m:ss" ("h:mm:ss" past an hour); "–" when there is no duration.
const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "–";
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${secs}` : `${minutes}:${secs}`;
};

const Dashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // The department / direction filters live in the navbar, next to the date
  // picker; the dashboard only reads their current value to drive the fetch.
  const { callsDateRange, setCallsDateRange, dashboardDepartment, dashboardDirection } =
    useAppContext();

  const { calls, companies, loading_calls } = useSelector((state) => state.analytics.analytics);

  const {
    scopes,
    company_config,
    loading: algoliaLoading,
  } = useSelector((state) => state.algolia.algolia);

  const display_tz = company_config?.display_tz;
  const { user } = useSelector((state) => state.user);

  const totalCalls = calls?.total_calls || 0;
  const frustratedClients = calls?.frustrated_clients || 0;
  const answerphone = calls?.answerphone || 0;
  const outgoingCalls = calls?.outgoing_calls || 0;
  const outgoingAnswerphone = calls?.outgoing_answerphone || 0;
  // Only the outgoing count is aggregated backend-side; incoming is the remainder.
  const incomingCalls = Math.max(0, totalCalls - outgoingCalls);
  const pickupRate =
    outgoingCalls > 0
      ? Math.round(((outgoingCalls - outgoingAnswerphone) / outgoingCalls) * 100)
      : 0;
  // Average duration of the calls carrying one, answering machine excluded
  // (backend-side); null when no call in the period has a duration.
  const avgDuration = calls?.avg_call_duration_seconds ?? null;
  const avgIncomingDuration = calls?.avg_incoming_call_duration_seconds ?? null;
  const avgOutgoingDuration = calls?.avg_outgoing_call_duration_seconds ?? null;
  const durationCaption =
    avgIncomingDuration !== null && avgOutgoingDuration !== null
      ? `${t("Incoming")} ${formatDuration(avgIncomingDuration)} · ${t(
          "Outgoing"
        )} ${formatDuration(avgOutgoingDuration)}`
      : t("Excluding answering machine");
  const shareOfCalls = (value) => (totalCalls > 0 ? Math.round((value / totalCalls) * 100) : 0);

  // Conditional accents: pickup rate is "higher is better", répondeur rate is
  // "lower is better" (inverted bands).
  const answerphoneRate = shareOfCalls(answerphone);
  const pickupAccent = pickupRate >= 80 ? "high" : pickupRate >= 50 ? "medium" : "low";
  const answerphoneAccent = answerphoneRate < 10 ? "high" : answerphoneRate < 25 ? "medium" : "low";

  const teamsScope = scopes?.teams || [];
  const departmentsScope = scopes?.departments || [];
  const localisationsScope = scopes?.localisations || [];
  // const hasGlobalVisibility = teamsScope.includes("all") || departmentsScope.includes("all") || localisationsScope.includes("all");
  const hasSimpleVisibility =
    teamsScope?.length === 0 && departmentsScope?.length === 0 && localisationsScope?.length === 0;

  // Skip the analytics refetch when the effective inputs (date range, timezone,
  // visibility, filters) are unchanged, so re-renders / user-object churn don't
  // refire it.
  const lastFetchKeyRef = useRef(null);
  useEffect(() => {
    if (!callsDateRange?.startDate || !callsDateRange?.endDate) return;
    const fetchKey = `${callsDateRange.startDate}|${callsDateRange.endDate}|${display_tz}|${hasSimpleVisibility}|${dashboardDepartment}|${dashboardDirection}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    dispatch(
      initAnalytics({
        start_date: transformToUTC(callsDateRange.startDate, display_tz),
        end_date: transformToUTC(callsDateRange.endDate, display_tz),
        department_id: dashboardDepartment,
        direction: dashboardDirection,
      })
    );
  }, [
    callsDateRange,
    user,
    hasSimpleVisibility,
    display_tz,
    dashboardDepartment,
    dashboardDirection,
  ]);

  return (
    <>
      <div className="px-[20px] pt-[20px]">
        <CallsGaugeBar />
      </div>
      {/* Five cards: three rate/count KPIs on the first row, then the two
          "how calls go" cards (duration, direction) side by side (xl: 6 columns,
          spans 2 / 3). On md the direction card takes the last row alone. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-[20px] px-[20px] pt-[20px]">
        <div className="xl:col-span-2">
          <KpiStatCard
            icon={MdOutlineWarningAmber}
            label="Frustrated Clients"
            value={frustratedClients}
            accent="low"
            progress={shareOfCalls(frustratedClients)}
            caption={`${shareOfCalls(frustratedClients)}% ${t("of calls")}`}
            loading={loading_calls}
            noData={totalCalls === 0}
            onClick={() => navigate("/calls?filter=frustrated")}
          />
        </div>
        <div className="xl:col-span-2">
          <KpiStatCard
            icon={MdOutlinePhoneMissed}
            label="Answering Machine"
            value={answerphone}
            accent={answerphoneAccent}
            progress={answerphoneRate}
            caption={`${answerphoneRate}% ${t("of calls")}`}
            loading={loading_calls}
            noData={totalCalls === 0}
            onClick={() => navigate("/calls?filter=answerphone")}
          />
        </div>
        <div className="xl:col-span-2">
          <KpiStatCard
            icon={MdOutlinePhoneForwarded}
            label="Outbound Pickup Rate"
            value={pickupRate}
            suffix="%"
            accent={pickupAccent}
            progress={pickupRate}
            caption={t("of outbound calls answered")}
            loading={loading_calls}
            noData={outgoingCalls === 0}
          />
        </div>
        <div className="xl:col-span-3">
          <KpiStatCard
            icon={MdOutlineTimer}
            label="Average call duration"
            value={formatDuration(avgDuration)}
            accent={avgDuration === null ? "neutral" : "primary"}
            caption={durationCaption}
            showProgress={false}
            loading={loading_calls}
            noData={totalCalls === 0}
          />
        </div>
        <div className="md:col-span-2 xl:col-span-3">
          <CallDirectionCard
            incoming={incomingCalls}
            outgoing={outgoingCalls}
            loading={loading_calls}
            onSelect={(direction) => navigate(`/calls?filter=call_direction&params=${direction}`)}
          />
        </div>
      </div>
      <div className="flex flex-wrap xl:flex-nowrap w-full gap-[20px] px-[20px] pt-[20px]">
        <div className="grow bg-white rounded-md px-[32px] py-[20px] overflow-hidden shadow-custom-light">
          <LineChartSection
            title={hasSimpleVisibility ? "Grade" : "Company Grade"}
            data={calls}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-[20px] p-[20px]">
        {!hasSimpleVisibility && (
          <div className="col-span-full xl:col-span-6">
            <TopFlopTable />
          </div>
        )}
        {!hasSimpleVisibility && (
          <div className="col-span-full xl:col-span-6">
            <BarChartSection title="Calls by Department" />
          </div>
        )}
        <div className="col-span-full xl:col-span-6">
          <CallPurposeSection title="Calls by Purpose" />
        </div>
        <div className="col-span-full">
          <CallsHeatmapSection />
        </div>
      </div>
    </>
  );
};

export default Dashboard;
